import { Alert, AlertDescription, AlertTitle } from '@tempo-io/sui-alert';
import { useTranslation } from 'react-i18next';
import { BudgetSection } from './BudgetSection';
import { CostSection } from './CostSection';
import { computeEffectiveAmount, useFinancialFormat } from './financialPanelLayout';
import { RevenueSection } from './RevenueSection';
import type { FinancialSaveState, ItemFinancialFigures } from './types';
import { useLiveDraft } from './useLiveDraft';

const IDLE_SAVE_STATE: FinancialSaveState = { status: 'idle' };

interface ItemFinancialPanelViewProps {
  amount: number | null;
  currencyCode: string | null;
  isLoading: boolean;
  isReadOnly: boolean;
  isLowestLevel?: boolean;
  saveState: FinancialSaveState;
  onSave: (amount: number | null) => void;
  figures?: ItemFinancialFigures;
  onCapexPercentChange?: (value: number | null) => void;
  capexSaveState?: FinancialSaveState;
  onForecastedRevenueChange?: (value: number | null) => void;
  revenueSaveState?: FinancialSaveState;
}

export const ItemFinancialPanelView = ({
  amount,
  currencyCode,
  isLoading,
  isReadOnly,
  isLowestLevel = false,
  saveState,
  onSave,
  figures,
  onCapexPercentChange,
  capexSaveState = IDLE_SAVE_STATE,
  onForecastedRevenueChange,
  revenueSaveState = IDLE_SAVE_STATE,
}: ItemFinancialPanelViewProps) => {
  const { t } = useTranslation();
  const { symbol, money, rate } = useFinancialFormat(currencyCode);
  const [liveAmount, setLiveAmount] = useLiveDraft(amount);

  const below = figures?.descendantsBudgetAmount ?? null;
  const effectiveBudget = figures ? computeEffectiveAmount(isLowestLevel, liveAmount, below) : null;

  return (
    <div className="flex cursor-default flex-col gap-4">
      {isReadOnly && (
        <Alert variant="info" data-testid="financial-read-only">
          <AlertDescription>{t(($) => $.financials.panel.readOnly)}</AlertDescription>
        </Alert>
      )}

      <BudgetSection
        amount={amount}
        figures={figures}
        effectiveBudget={effectiveBudget}
        symbol={symbol}
        money={money}
        isReadOnly={isReadOnly}
        isLoading={isLoading}
        isLowestLevel={isLowestLevel}
        saveState={saveState}
        onSave={onSave}
        onAmountChange={setLiveAmount}
        onCapexPercentChange={onCapexPercentChange}
        capexSaveState={capexSaveState}
      />

      <RevenueSection
        figures={figures}
        symbol={symbol}
        money={money}
        isReadOnly={isReadOnly}
        isLoading={isLoading}
        isLowestLevel={isLowestLevel}
        saveState={revenueSaveState}
        onForecastedRevenueChange={onForecastedRevenueChange}
      />

      <CostSection
        figures={figures}
        effectiveBudget={effectiveBudget}
        money={money}
        rate={rate}
        isLowestLevel={isLowestLevel}
      />

      {!figures && (
        <Alert variant="info" data-testid="financial-coming-soon">
          <AlertTitle>{t(($) => $.financials.panel.comingSoonTitle)}</AlertTitle>
          <AlertDescription>{t(($) => $.financials.panel.comingSoonDescription)}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
